import React, { useState } from "react";
import { api, isTransportFailure } from "@/lib/api";
import { uploadVideoChunked } from "@/lib/uploadVideo";
import { LinkIcon, Film, X } from "lucide-react";

export default function AddMeetingForm({ onCreated, onCancel }) {
    const [title, setTitle] = useState("");
    const [mode, setMode] = useState("canva");
    const [canvaUrl, setCanvaUrl] = useState("");
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [sourceUrl, setSourceUrl] = useState("");
    const [sourceNote, setSourceNote] = useState("");

    const submit = async () => {
        setErr(null);
        if (!title.trim()) { setErr("Give the meeting a title."); return; }
        if (mode === "canva" && !canvaUrl.trim()) { setErr("Paste the Canva share link."); return; }
        if (mode === "video" && !file) { setErr("Choose an MP4 to upload."); return; }
        setBusy(true);
        try {
            const citation = { source_url: sourceUrl.trim() || undefined, source_note: sourceNote.trim() || undefined };
            if (mode === "canva") {
                await api.post("/safety/meetings", { title, source_type: "canva", canva_url: canvaUrl, ...citation });
            } else {
                const uploadId = await uploadVideoChunked(file, setProgress);
                // This call is not a small JSON POST: the server reads the
                // assembled file off disk and pushes it to object storage
                // before it answers, so it is held open for as long as that
                // takes. The default 15s timeout aborts it on any sizeable
                // video -- after the upload has already succeeded, which is
                // the most confusing moment possible to fail.
                await api.post("/safety/meetings", { title, source_type: "video", upload_id: uploadId, original_filename: file.name, ...citation },
                    { timeout: 180000 });
            }
            onCreated();
        } catch (e) {
            // A timeout or a dropped connection carries no response, so there
            // is no server message to show. Saying "could not create the
            // meeting" for that case describes the wrong problem: nothing was
            // rejected, the upload ran out of time. Name it, so the next step
            // is obvious instead of mysterious.
            setErr(
                e?.response?.data?.detail ||
                (isTransportFailure(e)
                    ? "The upload did not finish — the connection dropped or ran out of time. On a slow connection a large video can take several minutes; try again, or try a shorter clip."
                    : "Could not create the meeting.")
            );
        } finally {
            setBusy(false);
            setProgress(0);
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card p-5 space-y-4" data-testid="add-meeting-form">
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-admin-header font-bold">New safety meeting</div>
                <button onClick={onCancel} className="p-1 rounded-lg hover:bg-brand-stone" data-testid="add-meeting-cancel"><X className="h-4 w-4" /></button>
            </div>

            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title — e.g. Ladder Safety (June 23)"
                data-testid="meeting-title-input"
                className="w-full h-12 border border-brand-charcoal/20 rounded-xl px-3 focus:ring-2 focus:ring-brand-admin-header outline-none" />

            <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setMode("canva")} data-testid="mode-canva"
                    className={`h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border transition ${mode === "canva" ? "bg-brand-charcoal text-brand-bone border-brand-charcoal" : "bg-white text-brand-charcoal border-brand-charcoal/20"}`}>
                    <LinkIcon className="h-4 w-4" /> Canva link
                </button>
                <button onClick={() => setMode("video")} data-testid="mode-video"
                    className={`h-12 rounded-xl font-bold text-sm flex items-center justify-center gap-2 border transition ${mode === "video" ? "bg-brand-charcoal text-brand-bone border-brand-charcoal" : "bg-white text-brand-charcoal border-brand-charcoal/20"}`}>
                    <Film className="h-4 w-4" /> Upload MP4
                </button>
            </div>

            {mode === "canva" ? (
                <div className="space-y-1">
                    <input value={canvaUrl} onChange={(e) => setCanvaUrl(e.target.value)} placeholder="https://www.canva.com/design/…/view"
                        data-testid="canva-url-input"
                        className="w-full h-12 border border-brand-charcoal/20 rounded-xl px-3 focus:ring-2 focus:ring-brand-admin-header outline-none" />
                    <p className="text-xs text-brand-charcoal/50">In Canva: Share → Public view link → Copy. Crew tap to advance slides.</p>
                </div>
            ) : (
                <div className="space-y-1">
                    <input type="file" accept="video/mp4,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
                        data-testid="video-file-input"
                        className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-brand-charcoal file:text-brand-bone file:font-bold" />
                    <p className="text-xs text-brand-charcoal/50">Export your Canva as MP4 (with audio), then upload. Big files are fine — they just take longer.</p>
                    {busy && mode === "video" && (
                        <div className="mt-2 h-2 rounded-full bg-brand-charcoal/10 overflow-hidden" data-testid="upload-progress">
                            <div className="h-full bg-brand-admin-header transition-all" style={{ width: `${progress}%` }} />
                        </div>
                    )}
                </div>
            )}

            <div className="space-y-2 pt-1 border-t border-brand-charcoal/10">
                <div className="text-[10px] uppercase tracking-widest text-brand-admin-header font-bold pt-2">Source (optional, for compliance records)</div>
                <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="Source URL — e.g. https://www.osha.gov/…"
                    data-testid="source-url-input"
                    className="w-full h-12 border border-brand-charcoal/20 rounded-xl px-3 focus:ring-2 focus:ring-brand-admin-header outline-none" />
                <textarea value={sourceNote} onChange={(e) => setSourceNote(e.target.value)} placeholder="Note — e.g. OSHA Toolbox Talks, adapted for our crew"
                    rows={2} data-testid="source-note-input"
                    className="w-full border border-brand-charcoal/20 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-brand-admin-header outline-none resize-none" />
                <p className="text-xs text-brand-charcoal/50">Where this training content came from — shown on the compliance report.</p>
            </div>

            {err && <div className="text-sm text-brand-danger" data-testid="add-meeting-error">{err}</div>}

            <button onClick={submit} disabled={busy} className="w-full h-12 rounded-xl bg-brand-admin-header hover:bg-[#6E1824] text-brand-bone font-bold disabled:opacity-60" data-testid="add-meeting-submit">
                {busy ? (mode === "video" ? `Uploading… ${progress}%` : "Posting…") : "Post meeting"}
            </button>
        </div>
    );
}
